/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react'

import classnames from 'classnames'
import { Link, useHistory, useRouteMatch } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import { api } from '../api'
import { useQuery } from '../utils/hooks/useQueryParam'
import { Logger } from '../utils/Logger'
import { chatSocket } from '../utils/socket'

export const ChannelsList: React.FC = () => {
  const [loading, setLoading] = React.useState(true)
  const [channels, setChannels] = React.useState<Api_Channels.Channel[]>([])

  const history = useHistory()
  const channelId = useRouteMatch<{ channelId: string }>('/channels/:channelId')
    ?.params.channelId
  const query = useQuery()
  const channelQuery = query.get('channelQuery')
  const channelStatus = (query.get('channelStatus') ?? 'active') as
    | 'active'
    | 'archived'
    | 'all'

  const listChannels = React.useCallback(() => {
    setLoading(true)
    api
      .listChannels({
        status: channelStatus,
        query: channelQuery || undefined,
      })
      .then((newChannels) => {
        setChannels(newChannels)
        setLoading(false)
      })
      .catch((err) => {
        Logger.error(err)
      })
  }, [channelQuery, channelStatus])

  React.useEffect(() => {
    listChannels()
  }, [listChannels])

  const onChangeStatus = React.useCallback(
    (newStatus: 'active' | 'archived' | 'all') => {
      return () => {
        setChannels([])
        query.set('channelStatus', newStatus)
        history.push(`${history.location.pathname}?${query.toString()}`)
      }
    },
    [query, history],
  )

  const debounced = useDebouncedCallback((value: string) => {
    query.set('channelQuery', value)
    history.push(`${history.location.pathname}?${query.toString()}`)
  }, 250)

  const onUpdateSearchQuery = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLoading(true)
      debounced.callback(event.target.value)
    },
    [debounced],
  )

  React.useEffect(() => {
    const listener = (messageData: any) => {
      listChannels()
    }
    chatSocket.on('message', listener)
    return () => {
      chatSocket.off('message', listener)
    }
  }, [listChannels])

  return (
    <nav className="panel" style={{ backgroundColor: 'white' }}>
      <p className="panel-heading">Channels</p>
      <div className="panel-block">
        <p className="control has-icons-left">
          <input
            className="input is-small"
            type="text"
            placeholder="Search"
            onChange={onUpdateSearchQuery}
          />
          <span className="icon is-left">
            <i
              className={classnames('fas', {
                'fa-spinner-third fa-spin': loading,
                'fa-search': !loading,
              })}
              aria-hidden="true"
            />
          </span>
        </p>
      </div>
      <p className="panel-tabs is-fullwidth" style={{ width: '100%' }}>
        <a
          onClick={onChangeStatus('active')}
          style={{ width: '100%', textAlign: 'center' }}
          className={classnames({
            'is-active': !['archived', 'all'].includes(channelStatus),
          })}>
          Opened
        </a>
        <a
          onClick={onChangeStatus('archived')}
          style={{ width: '100%', textAlign: 'center' }}
          className={classnames({ 'is-active': channelStatus === 'archived' })}>
          Archived
        </a>
        <a
          onClick={onChangeStatus('all')}
          style={{ width: '100%', textAlign: 'center' }}
          className={classnames({ 'is-active': channelStatus === 'all' })}>
          All
        </a>
      </p>
      {channels.map((channel) => (
        <Link
          to={`/channels/${channel.id}?${query.toString()}`}
          key={channel.id}
          className={classnames('panel-block', {
            'is-active': channel.id === channelId,
          })}>
          <span className={'panel-icon'}>
            <i className="fas fa-user" aria-hidden="true" />
          </span>
          <span>
            {`${channel.creator.firstName} ${channel.creator.lastName}`}
          </span>
        </Link>
      ))}
    </nav>
  )
}
